import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import axios from 'axios';
import { UserContext } from './UserContext';

export const DeliveryRequestsContext = createContext({
  showQuotes: false,
  refreshDeliveryRequests: async () => {},
});

const DeliveryRequestsProvider = ({ children }) => {
  const { user } = useContext(UserContext);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [showQuotes, setShowQuotes] = useState(false);

  const fetchDeliveryRequests = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !user) {
        setShowQuotes(false);
        return false;
      }

      const res = await axios.get(`${apiUrl}/vessels/delivery/requests?sort_order=newest&page=1&per_page=10&filter=active&type=boat_owner`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const requests = res?.data?.deliveryRequests?.data;
      // console.log('DeliveryRequestsContext: fetched requests data', requests);
      const has = Array.isArray(requests) && requests.length > 0;
      setShowQuotes(has);
      // console.log('DeliveryRequestsContext: fetched delivery requests, has active:', has);
      return has;
    } catch (err) {
      console.error('DeliveryRequestsContext: failed to fetch delivery requests', err);
      setShowQuotes(false);
      return false;
    }
  }, [user, apiUrl]);

  useEffect(() => {
    // fetch initially when user becomes available
    fetchDeliveryRequests();
  }, [fetchDeliveryRequests]);

  useEffect(() => {
    // allow other components to trigger a refresh by dispatching a document event
    const handler = () => {
      fetchDeliveryRequests();
    };
    document.addEventListener('deliveryCreated', handler);
    return () => document.removeEventListener('deliveryCreated', handler);
  }, [fetchDeliveryRequests]);

  const refreshDeliveryRequests = useCallback(async () => {
    return fetchDeliveryRequests();
  }, [fetchDeliveryRequests]);

  const contextValue = useMemo(() => ({
    showQuotes,
    refreshDeliveryRequests,
  }), [showQuotes, refreshDeliveryRequests]);

  return (
    <DeliveryRequestsContext.Provider value={contextValue}>
      {children}
    </DeliveryRequestsContext.Provider>
  );
};

export const dispatchDeliveryCreatedEvent = () => {
  try {
    const ev = new Event('deliveryCreated');
    document.dispatchEvent(ev);
  } catch (err) {
    // fallback
    console.warn('Could not dispatch deliveryCreated event', err);
  }
};

export default DeliveryRequestsProvider;
